/* eslint-disable react-refresh/only-export-components -- intentional: step exports a helper alongside the component */
import { useEffect, useMemo } from 'react';
import { AlertCircle } from 'lucide-react';

import { cn } from '@/lib/cn';
import { useGetModelsQuery } from '@/services/avatarApi';

import type { WizardState } from '../types';

interface Props {
  state: WizardState;
  update: <K extends keyof WizardState>(k: K, v: WizardState[K]) => void;
}

/**
 * Renders the model + aspect + transparent toggles. The model list comes from
 * /avatars/models; we filter out deprecated entries. When the user picked a
 * source photo on Step 1, only Gemini-family models can accept it, so we
 * auto-clamp the selection.
 */
export function Step2RenderSetup({ state, update }: Props) {
  const { data: models = [], isLoading } = useGetModelsQuery();

  const availableModels = useMemo(
    () => models.filter(m => !m.deprecated),
    [models],
  );

  const hasPhoto = !!state.sourceImageDataUrl;
  const supportsPhoto = (modelId: string | undefined) => {
    if (!modelId) return false;
    return /^gemini-/.test(modelId) && modelId.includes('image');
  };

  const photoIncompatible = hasPhoto && state.model && !supportsPhoto(state.model);

  // On mount or when models load: clamp the model when a photo is present and
  // the chosen model can't accept it. Picks the first Gemini-image model in
  // the list (the backend factory routes by id prefix).
  useEffect(() => {
    if (!hasPhoto || availableModels.length === 0) return;
    if (state.model && supportsPhoto(state.model)) return;
    const fallback = availableModels.find(m => supportsPhoto(m.id));
    if (fallback) update('model', fallback.id);
  }, [hasPhoto, availableModels, state.model, update]);

  // Default model when nothing picked yet and no photo.
  useEffect(() => {
    if (state.model || availableModels.length === 0) return;
    const def = availableModels.find(m => m.isDefault) ?? availableModels[0];
    update('model', def.id);
  }, [state.model, availableModels, update]);

  return (
    <section className="flex flex-col gap-5">
      <header className="flex flex-col gap-1">
        <h1 className="text-xl font-bold text-fg">Pick the look</h1>
        <p className="text-sm text-fg-muted">
          Choose the AI model that will render your character, the canvas shape, and whether the output should have a transparent background.
        </p>
      </header>

      {/* Model */}
      <div className="flex flex-col gap-1.5">
        <label htmlFor="ed-model" className="text-xs font-semibold uppercase text-fg-muted">
          AI model
        </label>
        <select
          id="ed-model"
          value={state.model ?? ''}
          onChange={e => update('model', e.target.value)}
          disabled={isLoading}
          className="rounded-xl bg-bg-secondary px-3 py-2.5 text-sm text-fg focus:outline-none disabled:opacity-50"
        >
          {availableModels.map(m => {
            const incompatible = hasPhoto && !supportsPhoto(m.id);
            return (
              <option key={m.id} value={m.id} disabled={incompatible}>
                {m.name}{m.isDefault ? ' (default)' : ''}{incompatible ? ' — text only' : ''}
              </option>
            );
          })}
        </select>
        {photoIncompatible && (
          <p className="flex items-center gap-1.5 text-xs text-amber-400">
            <AlertCircle aria-hidden className="h-3.5 w-3.5" />
            A photo is set; switched to a Gemini image model automatically.
          </p>
        )}
      </div>

      {/* Aspect ratio */}
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-semibold uppercase text-fg-muted">Aspect ratio</label>
        <div className="flex gap-2">
          {(['1:1', '3:4'] as const).map(ar => (
            <button
              key={ar}
              type="button"
              onClick={() => update('aspectRatio', ar)}
              className={cn(
                'flex-1 rounded-xl border px-3 py-2.5 text-sm font-semibold transition-colors',
                state.aspectRatio === ar
                  ? 'border-brand bg-brand/10 text-fg'
                  : 'border-border bg-bg-secondary text-fg-muted',
              )}
            >
              {ar}
            </button>
          ))}
        </div>
      </div>

      {/* Transparent toggle */}
      <div className="flex flex-col gap-1.5">
        <label className="text-xs font-semibold uppercase text-fg-muted">Background</label>
        <button
          type="button"
          onClick={() => update('transparent', !state.transparent)}
          className={cn(
            'flex items-center justify-between rounded-xl bg-bg-secondary px-3 py-3 text-sm transition-opacity active:opacity-80',
          )}
        >
          <span className="flex flex-col items-start">
            <span className="font-semibold text-fg">
              {state.transparent ? 'Transparent (RGBA)' : 'Opaque (default scene)'}
            </span>
            <span className="text-xs text-fg-muted">
              Transparent works best for the Hub floating overlay. Only honored by Gemini-family models.
            </span>
          </span>
          <span
            aria-hidden
            className={cn(
              'relative h-5 w-9 rounded-full transition-colors',
              state.transparent ? 'bg-brand' : 'bg-bg-tertiary',
            )}
          >
            <span
              className={cn(
                'absolute top-0.5 h-4 w-4 rounded-full bg-white transition-transform',
                state.transparent ? 'translate-x-4' : 'translate-x-0.5',
              )}
            />
          </span>
        </button>
      </div>
    </section>
  );
}

export function step2Valid(state: WizardState): boolean {
  return !!state.model;
}
